<template>
  <v-container fluid>
    <v-row align="center">
      <v-col cols="12" md="4" v-if="isAdminUser">
        <BaseSelect
          class="ui-form-field"
          v-model="selectedArea"
          :items="areaOptions"
          label="Filtrar por área"
          clearable
          @change="filterByArea"
        />
      </v-col>
      <v-col cols="12" md="4" v-if="!isAdminUser">
        <v-select
          class="ui-form-field"
          v-model="selectedEstatus"
          :items="estatusOptions"
          background-color="transparent"
          color="secondary"
          rounded
          filled
          dense
          hide-details
          item-color="accent"
          item-text="text"
          item-value="value"
          label="Filtrar por Estatus de lectura"
          @change="filterByEstatus"
        />
      </v-col>
      <v-col cols="12" md="4">
        <BaseInput
          class="ui-form-field"
          v-model="searchQuery"
          label="Buscar notificaciones"
          clearable
          @input="filterNotifications"
        />
      </v-col>
      <v-col cols="auto" class="ml-auto">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <IconButton class="ui-download-btn" variant="secondary" icon="mdi-download" @click="downloadNotificationsCsv" />
            </span>
          </template>
          <span>Descargar CSV</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <v-row v-if="loading" justify="center" class="my-5">
      <v-col cols="12" class="text-center">
        <v-progress-circular indeterminate color="secondary" size="64" />
        <p class="mt-3">Cargando notificaciones...</p>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <NotificationsTable :notifications="filteredNotifications" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import NotificationsTable from '@/components/notifications/NotificationsTable.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import IconButton from '@/components/base/IconButton.vue'
import downloadCsv from '@/utils/downloadCsv'
import AREAS from '@/modules/notificaciones/domain/areas'
import { mapActions, mapGetters } from 'vuex'
import { PERFILES } from '@/perfiles'
import AREAS_PERFILES from '@/modules/notificaciones/domain/AreasPerfiles.js'

const AREA_NOTIFICACION_KEYS = {
  [AREAS.RECURSOS_HUMANOS]: 'recursos_humanos',
  [AREAS.ARMERIA]: 'armeria',
  [AREAS.EQUIPAMIENTO]: 'equipamiento',
  [AREAS.IFCSP]: 'ifcsp',
  [AREAS.DESARROLLO_POLICIAL]: 'desarrollo_policial',
  [AREAS.ENLACE_C3]: 'enlace_c3',
  [AREAS.REGISTRO_PERSONAL]: 'registro_personal',
  [AREAS.ENLACE_COMISARIAS]: 'enlace_comisarias',
  [AREAS.EJECUTIVO]: 'ejecutivo',
}

export default {
  components: { NotificationsTable, BaseSelect, BaseInput, IconButton },

  data() {
    return {
      selectedArea: null,
      selectedEstatus: null,
      searchQuery: '',
      filteredNotifications: [],
      PERFILES,
      estatusOptions: [
        { text: 'Leídas', value: 'read' },
        { text: 'No leídas', value: 'unread' },
      ],
    }
  },

  created() {
    this.fetchAll()
  },

  computed: {
    ...mapGetters('notificaciones', { notifications: 'all', loading: 'loading' }),

    areaOptions() {
      return Object.values(AREAS)
    },

    areaKey() {
      const perfil = this.$store.getters['getPerfil']
      for (const [areaKey, perfiles] of Object.entries(AREAS_PERFILES)) {
        if (Array.isArray(perfiles) && perfiles.includes(perfil)) {
          return areaKey
        }
      }
      return null
    },
  },

  watch: {
    notifications: {
      immediate: true,
      handler(newNotifications) {
        this.filteredNotifications = newNotifications
      },
    },
  },

  methods: {
    ...mapActions('notificaciones', ['fetchAll']),
    downloadNotificationsCsv() {
      const rows = (this.filteredNotifications || []).map((n) => ({
        Fecha: n.fecha || n.fechaHora || n.createdAt || n.created_at || '',
        Título: n.titulo || '',
        Mensaje: n.mensaje || '',
        Prioridad: n.prioridad?.title || n.prioridad || '',
        Categoría: n.categoria?.title || n.categoria || '',
      }))

      downloadCsv(rows, { filename: 'notificaciones' })
    },

    applyFilters() {
      let notifications = [...this.notifications]

      if (this.selectedArea) {
        notifications = notifications.filter((notification) => notification.titulo.includes(this.selectedArea))
      }

      if (this.selectedEstatus) {
        const keyProp = this.areaKey ? AREA_NOTIFICACION_KEYS[this.areaKey] : null
        if (keyProp) {
          notifications = notifications.filter((notification) => {
            if (this.selectedEstatus === 'read') {
              return notification[keyProp] === 2
            } else if (this.selectedEstatus === 'unread') {
              return notification[keyProp] !== 2
            }
            return true
          })
        }
      }

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const query = this.searchQuery.toLowerCase().trim()
        notifications = notifications.filter((notification) => {
          const titulo = notification.titulo?.toLowerCase() || ''
          const descripcion = notification.descripcion?.toLowerCase() || ''
          const mensaje = notification.mensaje?.toLowerCase() || ''

          return titulo.includes(query) || descripcion.includes(query) || mensaje.includes(query)
        })
      }

      this.filteredNotifications = notifications
    },

    filterByArea() {
      this.applyFilters()
    },

    filterByEstatus() {
      this.applyFilters()
    },

    filterNotifications() {
      this.applyFilters()
    },
  },
}
</script>

<style scoped>
/* Input de búsqueda y selects */
::v-deep .ui-form-field .v-input__slot {
  border-radius: 12px !important;
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.25) !important;
  background: rgba(16, 16, 30, 0.35) !important;
  transition: all 0.3s ease !important;
}

::v-deep .ui-form-field .v-input__slot:hover {
  border-color: rgba(var(--v-secondary-base-rgb), 0.45) !important;
}

::v-deep .ui-form-field .v-input__control {
  background: transparent !important;
  border-radius: 12px;
  border: none !important;
  box-shadow: none !important;
}

::v-deep .ui-form-field.v-input--is-focused .v-input__control {
  border: none !important;
  box-shadow: none !important;
}

::v-deep .ui-form-field input {
  color: #e0e0e0 !important;
}

::v-deep .ui-form-field input::placeholder {
  color: rgba(224, 224, 224, 0.5) !important;
}

::v-deep .ui-form-field .v-label,
::v-deep .ui-form-field .mdi-magnify,
::v-deep .ui-form-field .v-icon {
  color: rgba(var(--v-secondary-base-rgb), 0.75) !important;
}

::v-deep .ui-form-field.v-input--is-focused .v-label {
  color: var(--v-secondary-base) !important;
}

/* Botón de descarga */
::v-deep .ui-download-btn.v-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
}

::v-deep .ui-download-btn.v-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(var(--v-secondary-base-rgb), 0.6) !important;
}

/* Tooltips */
::v-deep .v-tooltip__content {
  background: rgba(13, 27, 46, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(var(--v-secondary-base-rgb), 0.3);
  border-radius: 8px;
  color: #e0e0e0 !important;
  font-weight: 500;
  padding: 8px 12px;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(10, 10, 10, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: rgba(var(--v-secondary-base-rgb), 0.4);
  border-radius: 4px;
  transition: background 0.3s;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-secondary-base-rgb), 0.6);
}
</style>
